/**
 * Finds docIds that are used more than once within the same export scope of a form template.
 *
 * A docId names a content control (DOCX) or field (PDF/Excel) in the export document. Two top-level fields with the same
 * docId, or two columns of the same table with the same docId, both write to one place in the document, so the last one
 * wins and everything else in that row looks shifted (see Export-Issue.md). Export actions that create an extra
 * document field from a docId in exportData (INSERT_OPTION_LABEL_BEFORE, DUPLICATE_FIELD_WITH_NEW_DOCID) take part in
 * the same check, since they compete for the same document slots.
 */

export interface DocIdFieldTemplateLike {
	id?: string;
	header?: string | null;
	docId?: string | null;
	exportActions?: string[] | null;
	exportData?: string[] | null;
	tableFields?: { items?: DocIdFieldTemplateLike[] | null } | null;
}

export interface DuplicateDocId {
	/** "form" for top-level fields, otherwise the header of the table whose columns clash */
	scope: string;
	docId: string;
	/** The headers of the fields (or "<header> (label column)" for action-created fields) that share the docId */
	headers: string[];
}

/** Export actions whose exportData entry is a docId for a new document field. */
const DOC_ID_CREATING_ACTIONS = new Set(["INSERT_OPTION_LABEL_BEFORE", "DUPLICATE_FIELD_WITH_NEW_DOCID"]);

function collectDocIds(fieldTemplates: DocIdFieldTemplateLike[]): Map<string, string[]> {
	const byDocId = new Map<string, string[]>();
	const add = (docId: string | null | undefined, header: string) => {
		const trimmed = (docId || "").trim();
		if (!trimmed) return;
		const headers = byDocId.get(trimmed) || [];
		headers.push(header);
		byDocId.set(trimmed, headers);
	};
	for (const fieldTemplate of fieldTemplates) {
		const header = fieldTemplate.header || fieldTemplate.id || "(unnamed field)";
		add(fieldTemplate.docId, header);
		const actions = fieldTemplate.exportActions || [];
		const data = fieldTemplate.exportData || [];
		actions.forEach((action, index) => {
			if (DOC_ID_CREATING_ACTIONS.has(action)) add(data[index], `${header} (${action})`);
		});
	}
	return byDocId;
}

/**
 * @param topLevelFieldTemplates The form template's top-level field templates, with table columns attached under tableFields.items
 * @returns Every docId used more than once, per scope
 */
export function findDuplicateDocIds(topLevelFieldTemplates: DocIdFieldTemplateLike[]): DuplicateDocId[] {
	const duplicates: DuplicateDocId[] = [];
	const report = (scope: string, byDocId: Map<string, string[]>) => {
		byDocId.forEach((headers, docId) => {
			if (headers.length > 1) duplicates.push({ scope, docId, headers });
		});
	};
	report("form", collectDocIds(topLevelFieldTemplates));
	for (const fieldTemplate of topLevelFieldTemplates) {
		const columns = fieldTemplate.tableFields?.items || [];
		if (columns.length > 0) {
			report(fieldTemplate.header || fieldTemplate.id || "(unnamed table)", collectDocIds(columns));
		}
	}
	return duplicates;
}

/**
 * Formats duplicates for a warning message.
 */
export function describeDuplicateDocIds(duplicates: DuplicateDocId[]): string {
	return duplicates.map((d) => `${d.scope === "form" ? "Form" : `Table "${d.scope}"`}: docId "${d.docId}" is used by ${d.headers.join(" and ")}`).join(". ");
}

export default findDuplicateDocIds;
